/**
 * วิชาตัวเบาจากดันเจี้ยนและบอส (docs/archive/QINGGONG_DROPS_RED_LANTERN.md Part A). Four arts are no longer taught from the
 * start: each comes from **หน้าตำรา** (pages, goods) that bind into a **ตำราวิชาตัวเบา** (a whole manual), read to learn
 * level 1; a manual opens levels 1-3 (`selfCap`), levels 4 and up need the art's teacher. เหยียบเมฆาเหินฟ้า also needs
 * the three **ตราเมฆา** (one per world boss). Pages, manuals and seals are goods (`progress.trades.goods`), so they trade.
 *
 * Data only; the rules are systems/qinggongDrops.ts. All draws go through the World's Rng.
 */
import type { GoodDef } from './professions';
import type { WorldBossId } from './worldBosses';

export const qgPageId = (art: string): string => `qg-page-${art}`;
export const qgManualId = (art: string): string => `qg-manual-${art}`;
export const qgSealId = (boss: string): string => `qg-seal-${boss}`;

/** The arts that drop: pages to bind, and the teacher's zones (levels 4+ need a teacher in one of them). */
export const QG_ARTS: Readonly<Record<string, { readonly pages: number; readonly name: string; readonly manualName: string }>> = {
  'water-skim': { pages: 4, name: 'วิ่งบนน้ำ', manualName: 'ตำราวิ่งบนน้ำ' },
  'wall-run': { pages: 4, name: 'ไต่กำแพง', manualName: 'ตำราไต่ผา' },
  'feather-fall': { pages: 5, name: 'ร่อนลมขนนก', manualName: 'ตำราร่อนลมขนนก' },
  'gap-leap': { pages: 5, name: 'กระโจนข้ามเหว', manualName: 'ตำรากระโจนข้ามเหว' },
};
export const QG_SELF_CAP = 3;

/** The art each world boss drops pages of, and its seal. */
export const QG_BOSS_ART: Readonly<Record<WorldBossId, string>> = {
  'wb-ape': 'wall-run',
  'wb-firebird': 'feather-fall',
  'wb-thunder-turtle': 'gap-leap',
};
export const QG_SEAL_BOSSES: readonly WorldBossId[] = ['wb-ape', 'wb-firebird', 'wb-thunder-turtle'];

/** ถ้ำคลื่นคำราม (data/dungeon.ts): the room-3 boss (เจ้าคลื่นเงิน) and the chest drop water-skim pages. */
export const QG_DUNGEON = {
  art: 'water-skim',
  roomBoss: { monster: 'dg-silver-wave', page: 0.3 },
  chest: { page: 0.6, manual: 0.08 },
  /** Dry claims (a full chest or a boss kill that paid nothing) before the next one pays a page. */
  guard: 3,
} as const;

/** The world-boss chest rows (data/worldBosses.ts `WB_CHEST`): page chance by tier, the manual on gold, the seal on gold. */
export const QG_WB = {
  page: { gold: 0.5, silver: 0.35, copper: 0.25 },
  manual: { gold: 0.05 },
  /** After the day's full chests, a kill still rolls a page at this chance, with no guard. */
  late: 0.05,
  /** The third dry full chest pays a page. */
  guard: 3,
  seal: { chance: 0.1, guard: 5 },
} as const;

/** Keys of the bad-luck counters (`WorldBossSave.qg.pity`). */
export const pityKey = {
  dungeon: (art: string) => `dg:${art}`,
  boss: (boss: string) => `wb:${boss}`,
  seal: (boss: string) => `seal:${boss}`,
};

/** A save's pity counters repaired: whole numbers above 0 only; undefined when there are none. */
export function parseQgPity(raw: unknown): Record<string, number> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>))
    if (typeof v === 'number' && Number.isFinite(v) && v >= 1) out[k] = Math.min(99, Math.floor(v));
  return Object.keys(out).length ? out : undefined;
}

/** Where each dropped art comes from, for the qinggong panel (Thai). */
export const QG_SOURCES: Readonly<Record<string, readonly string[]>> = {
  'water-skim': ['ถ้ำคลื่นคำราม: เจ้าคลื่นเงินทิ้งหน้าตำรา 30% ต่อครั้ง', 'หีบท้ายถ้ำ: หน้าตำรา 60% ตำราทั้งเล่ม 8% (หีบที่สามที่ไม่ได้ จะได้แน่)'],
  'wall-run': ['ลิงยักษ์ทงปี้ (บอสโลก): หีบทอง 50% เงิน 35% ทองแดง 25% ได้หน้าตำรา ตำราทั้งเล่ม 5% จากหีบทอง'],
  'feather-fall': ['นกเพลิงปี้ฟาง (บอสโลก): หีบทอง 50% เงิน 35% ทองแดง 25% ได้หน้าตำรา ตำราทั้งเล่ม 5% จากหีบทอง', 'บททดสอบการร่อนของผู้เฒ่าเหยียบเมฆ'],
  'gap-leap': ['เต่าคะนองเหล่ยกุย (บอสโลก): หีบทอง 50% เงิน 35% ทองแดง 25% ได้หน้าตำรา ตำราทั้งเล่ม 5% จากหีบทอง'],
  'cloud-ascent': ['ตราเมฆา 1 ตราจากบอสโลกแต่ละตัว (หีบทอง 10% ตราที่ห้าที่ไม่ได้ จะได้แน่) รวมสามตราแล้วไปหาเจ้าอาวาสยอดหมอก'],
};

/** A drop the player was just paid (the qgDrop event): a page, a bound manual, a whole manual or a seal. */
export type QgDropKind = 'page' | 'bound' | 'manual' | 'seal';

/** The goods rows for GOODS (data/professions.ts): pages, manuals and seals. Spread into the goods list by the ITEMS owner. */
export const QG_GOODS: readonly GoodDef[] = [
  ...Object.entries(QG_ARTS).flatMap(([art, a]): GoodDef[] => [
    { id: qgPageId(art), name: `หน้าตำรา${a.name}`, kind: 'material', desc: `รวบรวมให้ครบ ${a.pages} หน้าเพื่อเย็บเป็น${a.manualName}`, price: 120 },
    { id: qgManualId(art), name: a.manualName, kind: 'material', desc: `อ่านเพื่อเรียน${a.name} ขั้น 1 (ฝึกได้ถึงขั้น ${QG_SELF_CAP} ขั้นสูงต้องให้ครูสอน)`, price: 600 },
  ]),
  ...QG_SEAL_BOSSES.map(
    (b): GoodDef => ({ id: qgSealId(b), name: 'ตราเมฆา', kind: 'material', desc: 'ตราจากผู้พิชิตบอสโลก ต้องรวมสามตราเพื่อเรียนเหยียบเมฆาเหินฟ้า', price: 900 }),
  ),
];
